import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort boundary for render-time errors. Network/API errors are handled
 * by React Query + ErrorState; this catches unexpected component crashes.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled UI error', error, info);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-ink-950 p-6">
          <div className="max-w-md rounded-xl bg-white dark:bg-ink-900 p-6 text-center shadow-sm ring-1 ring-slate-200 dark:ring-white/10">
            <h1 className="text-base font-semibold text-slate-900 dark:text-ink-50">Something went wrong</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-ink-400">
              The interface hit an unexpected error. Reload to continue.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
